import { useAppSelector } from '@/redux/hooks';
import { selectJob } from '@/redux/selectors/jobSelectors';
import { useEffect, useMemo, useRef, useState } from 'react';
import { selectActiveVersions as selectActiveVersionsV1 } from '@/redux/selectors/titleSelectors';
import { selectActiveVersions as selectActiveVersionsV2 } from '@/redux/selectors/titleV2Selectors';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig, TitleConfig } from '@/__configs/config_types';
import axios from '@/_utils/axios';
import { AssetComponent, AssetType, InventoryFileEntity } from '@/redux/slices/slice_types';
import { FileWithProxy } from '@/components/_common/VideoPlayerAdvanced/video-player.types';
import VideoPlayerAdvanced from '@/components/_common/VideoPlayerAdvanced/VideoPlayerAdvanced';
import { uniq } from 'lodash';
import useContentPlayback from '@/components/_common/VideoPlayerAdvanced/useContentPlayback';
import useFileType from '@/components/_hooks/useFileType';

type VideoWithDeliverablesProps = {
  id?: string;
  url: string;
  poster: string;
};

type FilesMap = Map<string, InventoryFileEntity>;

const VideoWithDeliverables = ({ id, url, poster }: VideoWithDeliverablesProps) => {
  const getFileGroup = useFileType();
  const job = useAppSelector(selectJob);
  const parseVersionsFn = useConfigValue<OrderPlacementConfig['parseVersionsFn']>(['orderPlacementConfig', 'parseVersionsFn']);
  const isTitleV2 = useConfigValue<TitleConfig['v2']>(['titleConfig', 'v2']);
  const versionsV1 = useAppSelector(selectActiveVersionsV1);
  const versionsV2 = useAppSelector(selectActiveVersionsV2);
  const isFetchingRef = useRef(false);
  const [externalAssets, setExternalAssets] = useState<FileWithProxy[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { getPlaybackSourceUrls, dolbyVideoUrl } = useContentPlayback();

  const assetComponents = useMemo(() => {
    if (job?.assets?.components?.length <= 0) {
      return [];
    }

    return job.assets.components;
  }, [job]);

  const getFiles = async (id: number, version: string, types: string[]) => {
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;

    const payload = {
      version_name_filter: [version],
      file_type_filter: types,
      file_status_filter: ['available', 'archive', 'different', 'future']
    };
    try {
      const response = await axios.post(`/inventory/titles/${id}/files/?page_size=999`, payload);

      const map: FilesMap = new Map();
      response.data?.data?.forEach((file: InventoryFileEntity) => {
        map.set(file.name, file);
      });

      return map;
    } catch (error) {
      console.error('Error fetching files:', error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  const getFileTypesFromAssets = (assets: AssetComponent[], wantedTypes: AssetType[]) => {
    const types = assets
      .filter((asset) => wantedTypes.includes(asset.type))
      .map((asset) => {
        const split = asset.filename.split('.');
        return split.pop();
      })
      .filter(Boolean) as string[];

    return uniq(types);
  };

  const getComponentsFiles = async (titleId: number, type: AssetType, filesMap: FilesMap, components: AssetComponent[]) => {
    const files = (
      await Promise.all(
        components.map(async (component) => {
          const file = filesMap.get(component.filename);
          if (!file) {
            return false;
          }

          return file;
        })
      )
    ).filter(Boolean) as InventoryFileEntity[];

    return { files, sourceUrls: await getPlaybackSourceUrls(files, titleId) };
  };

  const onDeliverablesChange = async (titleId: number, versionName: string, newAssets: AssetComponent[]) => {
    setIsLoading(true);
    try {
      if (newAssets.length === 0) {
        return;
      }

      const types = getFileTypesFromAssets(newAssets, ['subtitle', 'audio']);
      const files = await getFiles(titleId, versionName, types);

      if (!files) {
        return;
      }

      const { files: filesArray, sourceUrls } = await getComponentsFiles(titleId, 'audio', files, newAssets);

      const assets = filesArray.map((file) => ({
        ...file,
        asset_type: getFileGroup(file.file_type) as AssetType,
        proxy: {
          url: sourceUrls[file.id],
          filename: file.name
        }
      }));
      setExternalAssets(assets);
    } catch (error) {
      console.error('Error fetching file URLs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const versions = useMemo(() => {
    if (isTitleV2) {
      return versionsV2;
    }
    return versionsV1;
  }, [isTitleV2, versionsV1, versionsV2]);

  const jobVersion = useMemo(() => {
    if (!job || !job.output_version) {
      return undefined;
    }

    let jobOutputVersion = job.output_version;
    if (parseVersionsFn) {
      const [version] = parseVersionsFn([job.output_version]);
      jobOutputVersion = version;
    }

    if (jobOutputVersion) {
      return versions.find((v) => v.id === jobOutputVersion.id);
    }
  }, [job, versions, parseVersionsFn]);

  useEffect(() => {
    if (jobVersion) {
      onDeliverablesChange(jobVersion.root_parent, jobVersion.name, assetComponents);
    } else {
      setIsLoading(false);
    }
  }, [assetComponents, jobVersion]);

  return (
    <VideoPlayerAdvanced
      disableInventoryModal
      id={id}
      url={!isLoading ? dolbyVideoUrl || url : undefined}
      poster={poster}
      externalAssets={externalAssets}
    />
  );
};

export default VideoWithDeliverables;
